import { useState } from 'react'
import { loadLastRoom, loadToken } from '../client'
import { RulesSheet } from '../components/RulesSheet'

/**
 * 进场屏。
 *
 * 关于「刷新后自动重连」：这里**提示**而不是自动跳回去。
 * 一个刚刚主动退出的人再打开页面，不该被硬拽回原来那局 ——
 * 但一个手机切后台被回收的人，又确实需要一键回去。所以给按钮、不自动走。
 */
interface JoinScreenProps {
  onJoin(req: { code?: string; displayName: string; token?: string }): void
  pending: boolean
  error: string | null
}

export function JoinScreen({ onJoin, pending, error }: JoinScreenProps) {
  const [name, setName] = useState('')
  const [code, setCode] = useState('')
  const [rulesOpen, setRulesOpen] = useState(false)

  const lastRoom = loadLastRoom()
  const lastToken = lastRoom !== undefined ? loadToken(lastRoom) : undefined
  const canRejoin = lastRoom !== undefined && lastToken !== undefined

  const nameOk = name.trim().length > 0

  return (
    <div className="mx-auto flex min-h-full max-w-md flex-col justify-center gap-4 p-5">
      {rulesOpen && <RulesSheet onClose={() => setRulesOpen(false)} />}

      <h1 className="text-center text-2xl font-bold">三色暗棋</h1>
      <p className="text-center text-sm text-slate-400">三个人，各抽一张暗牌，谁先做成谁赢</p>

      <button
        type="button"
        onClick={() => setRulesOpen(true)}
        className="mx-auto min-h-[40px] rounded-full border border-slate-600 px-4 text-sm text-slate-300"
      >
        没玩过？先看看怎么玩
      </button>

      {error !== null && (
        <p role="alert" className="rounded-md bg-red-950/60 px-3 py-2 text-sm text-red-200">
          {error}
        </p>
      )}

      {canRejoin && (
        <button
          type="button"
          disabled={pending}
          onClick={() => onJoin({ code: lastRoom, displayName: name.trim() || '我', token: lastToken })}
          className="min-h-[52px] rounded-lg bg-emerald-600 text-base font-bold disabled:opacity-50"
        >
          回到上一局（房间 {lastRoom}）
        </button>
      )}

      <label className="text-sm">
        <span className="mb-1 block text-slate-300">你的昵称</span>
        <input
          value={name}
          onChange={(e) => setName(e.target.value)}
          maxLength={12}
          placeholder="怎么称呼你"
          className="min-h-[48px] w-full rounded-lg bg-slate-800 px-3 outline-none ring-1 ring-slate-600 focus:ring-amber-400"
        />
      </label>

      <label className="text-sm">
        <span className="mb-1 block text-slate-300">房间码（加入别人的局才填）</span>
        <input
          value={code}
          onChange={(e) => setCode(e.target.value.toUpperCase())}
          maxLength={4}
          placeholder="四位，例如 K7QM"
          // 房间码是大写字母数字：inputMode 让手机直接给大写键盘
          autoCapitalize="characters"
          className="min-h-[48px] w-full rounded-lg bg-slate-800 px-3 font-mono tracking-widest outline-none ring-1 ring-slate-600 focus:ring-amber-400"
        />
      </label>

      <button
        type="button"
        disabled={!nameOk || pending || code.trim() !== ''}
        onClick={() => onJoin({ displayName: name.trim() })}
        className="min-h-[52px] rounded-lg bg-amber-500 text-base font-bold text-slate-900 disabled:bg-slate-700 disabled:text-slate-500"
      >
        创建房间
      </button>

      <button
        type="button"
        disabled={!nameOk || pending || code.trim().length !== 4}
        onClick={() => onJoin({ code: code.trim(), displayName: name.trim() })}
        className="min-h-[52px] rounded-lg border border-slate-500 text-base font-bold disabled:opacity-40"
      >
        加入房间
      </button>
    </div>
  )
}
